import { FormikErrors } from "formik";
import i18next from "i18next";
import { NavigateFunction } from "react-router";
import { toast } from "react-toastify";

import React, { Fragment, createElement } from "react";

import routeRoutes from "components/_App/Routes/routes";

import { currentLanguage } from "consts";
import { colors } from "consts";

export const getLongestArray = (array: string[][]) => {
  return array.reduce((acc, curr) => {
    return acc.length > curr.length ? acc : curr;
  }, []);
};

export const trimStringInMiddle = (
  str: string,
  trimLength: number,
  maxLength: number
) => {
  if (str.length > maxLength) {
    return (
      str.substring(0, trimLength) +
      "..." +
      str.substring(str.length - trimLength, str.length)
    );
  }
  return str;
};

export const trimStringInTheEnd = (str: string, maxLength: number) => {
  if (str.length > maxLength) {
    return str.substring(0, maxLength) + "...";
  }
  return str;
};

export const calcItemProgress = (dueDate: string) => {
  const today = new Date();
  const deadline = new Date(dueDate);
  const diff = deadline.getTime() - today.getTime();
  const diffDays = Math.ceil(diff / (1000 * 60 * 60 * 24));
  const progress = 100 - (diffDays / 30) * 100;
  return progress;
};

export const getDeviceInfo = async () => {
  const devices = await navigator.mediaDevices.enumerateDevices();

  return {
    audioInputDevices: devices.filter((device) => device.kind === "audioinput"),
    videoInputDevices: devices.filter((device) => device.kind === "videoinput"),
    audioOutputDevices: devices.filter(
      (device) => device.kind === "audiooutput"
    ),
    hasAudioInputDevices: devices.some(
      (device) => device.kind === "audioinput"
    ),
    hasVideoInputDevices: devices.some(
      (device) => device.kind === "videoinput"
    ),
  };
};

export const isPermissionDenied = async (name: "camera" | "microphone") => {
  const permissionName = name as PermissionName;

  if (navigator.permissions) {
    try {
      const result = await navigator.permissions.query({
        name: permissionName,
      });
      return result.state === "denied";
    } catch {
      return false;
    }
  } else {
    return false;
  }
};

export const makeInitials = (name: string) => {
  let result = "";
  const splitedName = name.split(" ");
  if (splitedName[0]) {
    result += splitedName[0][0];
  }
  if (splitedName[1]) {
    result += splitedName[1][0];
  }
  return result.toLocaleUpperCase();
};

export const progressColor = (progress: number) => {
  switch (true) {
    case progress < 25:
      return colors.greenLight;
    case progress < 50:
      return colors.gold;
    case progress < 75:
      return colors.goldDark;
    default:
      return colors.red;
  }
};

export const onlyNumbers = <T>(list: T[]) => {
  return list.every((element) => {
    return typeof +element === "number";
  });
};

export const reorderDraggedList = <T>(items: React.ReactNode[], list: T[]) => {
  const reorderIndexes = items.map((item) => {
    if (React.isValidElement(item)) {
      return item.key;
    }
    return item;
  });
  return onlyNumbers(reorderIndexes)
    ? reorderIndexes.map((i) => list[i as number])
    : list;
};

export const handleNoAcces = (navigate: NavigateFunction) => {
  navigate(routeRoutes.home);
  toast.error("You don't have access to this page");
};

export const handleFormikErrorToasts = <T>(errors: FormikErrors<T>) => {
  const errorValues = Object.values(errors);
  const standardErrors = errorValues.filter(
    (singleValue) => typeof singleValue === "string"
  );
  const nestedErrors = errorValues
    .filter((singleValue) => typeof singleValue === "object")
    // @ts-ignore
    .flatMap((errorObject) => errorObject.sections)
    .reduce((acc, err) => {
      const info: any[] = Object.values(err).filter(Boolean);
      const convertedInfo =
        info.length > 1
          ? info.map((error) => createElement("p", null, error))
          : info;
      return [...acc, convertedInfo];
    }, []);

  [...standardErrors, ...nestedErrors].forEach((text) => {
    const paragraph = createElement(
      typeof text === "string" ? "p" : Fragment,
      null,
      text
    );
    toast.error(paragraph);
  });
};

export const convertEnumValueToName = (value: string) => {
  return value.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
};

export const replaceBreakLines = (text: string) => {
  return text?.replace(/(?:\r\n|\r|\n)/g, "<br>");
};

export const easternArabicDigits = [
  "۰",
  "۱",
  "۲",
  "۳",
  "۴",
  "۵",
  "۶",
  "۷",
  "۸",
  "۹",
];

export const convertToEasternArabic = (num: number) => {
  return num
    .toString()
    .split("")
    .map((digit) => easternArabicDigits[+digit])
    .join("");
};

export const stringToBoolean = (value: string) => {
  if (value === "true") return true;
  if (value === "false") return false;
  return value;
};

export const firtsLetterToUpperCase = (word: string) =>
  `${word.charAt(0).toUpperCase()}${word.slice(1)}`;

export const getTranslation = (key: string, options?: any) =>
  i18next.getResource(currentLanguage, "translation", key, options);
